import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";

export namespace ComponentBounds {
  export type Props = {
    /** The storybook container whose story subjects are outlined. */
    $scope: HTMLElement;
    /** The scrolling viewport the outlines are clipped to. */
    $viewport: HTMLElement;
  };
}

type Rect = { x: number; y: number; w: number; h: number };
type Box = Rect & { tags: string[] };

// A development aid for the catalogue: every custom element a story renders
// gets an outline and a name tab, the way a collaborative editor marks another
// person's selection. The overlay is one fixed layer redrawn from client rects
// every frame while it is on — never a style injected into the components, so
// it cannot move the layout it is measuring, and it follows scrolling,
// expansion and animation inside any shadow root without per-case hooks.
@component({
  name: "catalogue-component-bounds",
})
export class ComponentBounds extends LonaWebComponent {
  private static readonly STORAGE_KEY = "ui-catalogue:component-bounds";
  /** Story subjects are the `component`-slotted children of these frames; the
   * frames themselves, the grid and the page chrome are the harness. */
  private static readonly FRAMES = "std-storybook-card, std-storybook-card-layout";
  private static readonly COLORS = [
    "#e5484d",
    "#d9570d",
    "#2b9a66",
    "#0d8a83",
    "#0b7ad1",
    "#3e63dd",
    "#6e56cf",
    "#c2298a",
  ];
  private static readonly LABEL_HEIGHT = 16;
  private static readonly LABEL_FONT =
    "500 10px ui-monospace, SFMono-Regular, Menlo, monospace";

  private $layer: HTMLElement = this.$("layer");
  private props: Option<ComponentBounds.Props> = null;
  private enabled = false;
  private frame = 0;
  private signature = "";
  private $outlines: HTMLElement[] = [];
  private $labels: HTMLElement[] = [];
  private labelWidths: Map<string, number> = new Map();
  private measure: Option<CanvasRenderingContext2D> = null;

  onChange: Option<(enabled: boolean) => void> = null;

  bind(props: ComponentBounds.Props): this {
    this.props = props;
    this.setEnabled(localStorage.getItem(ComponentBounds.STORAGE_KEY) === "1");
    return this;
  }

  toggle() {
    this.setEnabled(!this.enabled);
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (enabled) localStorage.setItem(ComponentBounds.STORAGE_KEY, "1");
    else localStorage.removeItem(ComponentBounds.STORAGE_KEY);
    this.toggleAttribute("enabled", enabled);
    this.sync();
    this.onChange?.(enabled);
  }

  connectedCallback() {
    super.connectedCallback();
    this.sync();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    super.disconnectedCallback();
  }

  private sync() {
    const run = this.enabled && this.props != null && this.isConnected;
    if (run && this.frame === 0) {
      this.frame = requestAnimationFrame(this.tick);
    } else if (!run) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.signature = "";
    }
  }

  private tick = () => {
    this.frame = requestAnimationFrame(this.tick);
    this.paint();
  };

  private paint() {
    if (this.props == null) return;
    const { $scope, $viewport } = this.props;
    const v = $viewport.getBoundingClientRect();
    const view: Rect = {
      x: Math.max(v.left, 0),
      y: Math.max(v.top, 0),
      w: Math.min(v.right, innerWidth) - Math.max(v.left, 0),
      h: Math.min(v.bottom, innerHeight) - Math.max(v.top, 0),
    };

    // All reads, then all writes: one layout per frame at most.
    const boxes = new Map<string, Box>();
    const root = $scope.shadowRoot ?? $scope;
    for (const $frame of root.querySelectorAll(ComponentBounds.FRAMES)) {
      // The grid keeps a screen or so of cards connected past the viewport;
      // walking their trees is most of the cost, so off-screen frames are skipped.
      if (!ComponentBounds.intersects($frame.getBoundingClientRect(), view)) {
        continue;
      }
      for (const $subject of $frame.children) {
        if ($subject.slot !== "component") continue;
        ComponentBounds.collect($subject, view, boxes);
      }
    }

    const signature = `${view.x},${view.y},${view.w},${view.h}|` +
      [...boxes.keys()].join(";") + "|" +
      [...boxes.values()].map((b) => b.tags.join(">")).join(";");
    if (signature === this.signature) return;
    this.signature = signature;

    this.$layer.style.clipPath = `inset(${view.y}px ${
      innerWidth - view.x - view.w
    }px ${innerHeight - view.y - view.h}px ${view.x}px)`;

    const placed: Rect[] = [];
    let i = 0;
    for (const box of boxes.values()) {
      const color = ComponentBounds.colorOf(box.tags[0]);
      const $outline = this.$outlines[i] ??= this.$make("outline");
      $outline.style.display = "";
      $outline.style.setProperty("--bounds-color", color);
      $outline.style.transform = `translate(${box.x}px, ${box.y}px)`;
      $outline.style.width = `${box.w}px`;
      $outline.style.height = `${box.h}px`;

      const text = box.tags.join(" › ");
      const label = this.placeLabel(box, this.labelWidth(text), view, placed);
      const $label = this.$labels[i] ??= this.$make("label");
      $label.style.display = "";
      $label.style.setProperty("--bounds-color", color);
      $label.style.transform = `translate(${label.x}px, ${label.y}px)`;
      $label.toggleAttribute("inside", label.y >= box.y);
      if ($label.textContent !== text) $label.textContent = text;
      i++;
    }
    for (let j = i; j < this.$outlines.length; j++) {
      this.$outlines[j].style.display = "none";
      this.$labels[j].style.display = "none";
    }
  }

  /** Every visible custom element under `$el`, through shadow roots, in tree
   * order (outer first). Elements sharing exact bounds — a host that is all
   * its one child — merge into one outline with a chained name. */
  private static collect($el: Element, view: Rect, out: Map<string, Box>) {
    if ($el instanceof HTMLElement && $el.localName.includes("-")) {
      const r = $el.getBoundingClientRect();
      if (r.width >= 1 && r.height >= 1 && ComponentBounds.intersects(r, view)) {
        const box = {
          x: Math.round(r.left),
          y: Math.round(r.top),
          w: Math.round(r.width),
          h: Math.round(r.height),
        };
        const key = `${box.x},${box.y},${box.w},${box.h}`;
        const existing = out.get(key);
        if (existing) existing.tags.push($el.localName);
        else out.set(key, { ...box, tags: [$el.localName] });
      }
    }
    if ($el.shadowRoot) {
      for (const $c of $el.shadowRoot.children) {
        ComponentBounds.collect($c, view, out);
      }
    }
    for (const $c of $el.children) ComponentBounds.collect($c, view, out);
  }

  private static intersects(r: DOMRect, view: Rect): boolean {
    return r.right > view.x && r.left < view.x + view.w &&
      r.bottom > view.y && r.top < view.y + view.h;
  }

  /** The tab sits on the outline's top-left edge like a collaborator's name.
   * Nested components share edges, so a tab that would cover an earlier one
   * tries the top-right edge, and only then the inside corners, which cover
   * the component's own content. */
  private placeLabel(box: Box, width: number, view: Rect, placed: Rect[]) {
    const h = ComponentBounds.LABEL_HEIGHT;
    const right = box.x + box.w - width;
    const candidates = [
      { x: box.x, y: box.y - h },
      { x: right, y: box.y - h },
      { x: box.x, y: box.y },
      { x: right, y: box.y },
    ];
    const fits = (c: { x: number; y: number }) =>
      c.y >= view.y &&
      !placed.some((p) =>
        c.x < p.x + p.w && c.x + width > p.x && c.y < p.y + p.h && c.y + h > p.y
      );
    const chosen = candidates.find(fits) ?? candidates[2];
    placed.push({ ...chosen, w: width, h });
    return chosen;
  }

  private labelWidth(text: string): number {
    let width = this.labelWidths.get(text);
    if (width == null) {
      this.measure ??= document.createElement("canvas").getContext("2d");
      if (this.measure) this.measure.font = ComponentBounds.LABEL_FONT;
      // 5px of padding each side, matching `.label`.
      width = Math.ceil((this.measure?.measureText(text).width ?? text.length * 6) + 10);
      this.labelWidths.set(text, width);
    }
    return width;
  }

  private $make(className: string): HTMLElement {
    const $e = document.createElement("div");
    $e.className = className;
    this.$layer.appendChild($e);
    return $e;
  }

  /** One stable colour per tag, so the same component reads the same everywhere. */
  private static colorOf(tag: string): string {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = (hash * 31 + tag.charCodeAt(i)) | 0;
    }
    const colors = ComponentBounds.COLORS;
    return colors[Math.abs(hash) % colors.length];
  }

  static $styles = [
    css`
      :host {
        display: none;
        position: fixed;
        inset: 0px;
        pointer-events: none;
        /* Over the stories, under the page's own fixed controls (DEV_TOOLS). */
        z-index: 9400;
      }

      :host([enabled]) {
        display: block;
      }

      #layer {
        position: absolute;
        inset: 0px;
      }

      .outline,
      .label {
        position: absolute;
        top: 0px;
        left: 0px;
      }

      .outline {
        box-sizing: border-box;
        border: 1.5px solid var(--bounds-color);
        border-radius: 2px;
      }

      .label {
        box-sizing: border-box;
        height: 16px;
        padding: 0px 5px;
        border-radius: 3px 3px 3px 0px;
        background: var(--bounds-color);
        color: white;
        font: 500 10px/16px ui-monospace, SFMono-Regular, Menlo, monospace;
        white-space: nowrap;
      }

      .label[inside] {
        border-radius: 0px 0px 3px 0px;
      }
    `,
  ];

  static $html = html`<div id="layer"></div>`;
}
